import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ProductsPaginationBar from "../components/ProductsPaginationBar";
import ProductListSearchBar from "../components/ProductListSearchBar";
import { getTotalPages } from "../utils/paginationScroll";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizSearch, fetchActowizTopDeals } from "../store/actowiz/actowizSlice";
import { apiRequest } from "../api/client";
import { normalizeGroceryProductPlatform, rememberGroceryProductView } from "../utils/groceryProductView";

const BRAND_OPTIONS = [
  { key: "tesco", label: "Tesco" },
  { key: "asda", label: "Asda" },
  { key: "morrison", label: "Morrisons" },
  { key: "sainsbury", label: "Sainsbury’s" },
];

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function toQueryListParam(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  // Accept comma, line break, bullet, semicolon, or dash-separated values.
  // Example paste:
  // - milk
  // - bread
  // apple
  const parts = raw
    .split(/\r?\n|,|;|•|●|·|–|—|\s-\s|^\-\s+/gm)
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const unique = Array.from(new Set(parts));
  return unique.join(",");
}

function normalizeStoreKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (!p) return "";
  if (p.includes("tesco")) return "tesco";
  if (p.includes("asda")) return "asda";
  if (p.includes("morris")) return "morrison";
  if (p.includes("sains")) return "sainsbury";
  return "";
}

function SearchResults({ onOpenAuth, onOpenMenu }) {
  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const q = (query.get("q") || "").trim();
  const [pricingMode, setPricingMode] = useState("Base");

  const dispatch = useAppDispatch();
  const searchResults = useAppSelector((s) => s.actowiz.search);
  const searchStatus = useAppSelector((s) => s.actowiz.searchStatus);
  const searchError = useAppSelector((s) => s.actowiz.searchError);
  const searchTotal = useAppSelector((s) => s.actowiz.searchTotal);
  const topDealsResults = useAppSelector((s) => s.actowiz.topDeals);
  const topDealsStatus = useAppSelector((s) => s.actowiz.topDealsStatus);
  const topDealsError = useAppSelector((s) => s.actowiz.topDealsError);
  const topDealsTotal = useAppSelector((s) => s.actowiz.topDealsTotal);
  const allCatalog = useAppSelector((s) => s.actowiz.allCatalog);

  const [page, setPage] = useState(1);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [subCategoryOpen, setSubCategoryOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState(() => new Set());
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [selectedSubcategories, setSelectedSubcategories] = useState(() => new Set());
  const [editableQuery, setEditableQuery] = useState(q);
  const [noResultModalOpen, setNoResultModalOpen] = useState(false);
  const [dismissedNoResultQuery, setDismissedNoResultQuery] = useState("");
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const modeCacheRef = useRef({
    Base: { items: [], total: 0 },
    Offer: { items: [], total: 0 },
  });
  const hasNextRef = useRef(false);
  const pageSize = 20;
  const startIndex = (page - 1) * pageSize;
  const isOfferMode = pricingMode === "Offer";
  const results = isOfferMode ? topDealsResults : searchResults;
  const status = isOfferMode ? topDealsStatus : searchStatus;
  const error = isOfferMode ? topDealsError : searchError;
  const activeTotal = isOfferMode ? topDealsTotal : searchTotal;

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    setPage(1);
    setDismissedNoResultQuery("");
    setNoResultModalOpen(false);
    modeCacheRef.current = {
      Base: { items: [], total: 0 },
      Offer: { items: [], total: 0 },
    };
    hasNextRef.current = false;
  }, [q]);

  useEffect(() => {
    setPage(1);
    modeCacheRef.current = {
      Base: { items: [], total: 0 },
      Offer: { items: [], total: 0 },
    };
    hasNextRef.current = false;
  }, [selectedBrands, selectedCategories, selectedSubcategories]);

  useEffect(() => {
    setPage(1);
    hasNextRef.current = false;
  }, [pricingMode]);

  useEffect(() => {
    setEditableQuery(q);
  }, [q]);

  useEffect(() => {
    if (!q) return;
    if (isOfferMode) {
      dispatch(
        fetchActowizTopDeals({
          query_list: toQueryListParam(q),
          platform_list: selectedBrands.size ? Array.from(selectedBrands) : undefined,
          category: selectedCategories.size ? Array.from(selectedCategories) : undefined,
          sub_category: selectedSubcategories.size ? Array.from(selectedSubcategories) : undefined,
          start: startIndex,
          offset: pageSize,
          mode: "replace",
          force: true,
        })
      );
    } else {
      dispatch(
        fetchActowizSearch({
          query: q,
          query_list: toQueryListParam(q),
          platform_list: selectedBrands.size ? Array.from(selectedBrands) : undefined,
          category: selectedCategories.size ? Array.from(selectedCategories) : undefined,
          sub_category: selectedSubcategories.size ? Array.from(selectedSubcategories) : undefined,
          // Actowiz pagination expectation:
          // - `start` = starting index (0, 20, 40, ...)
          // - `offset` = page size (always 20 for our UI)
          start: startIndex,
          offset: pageSize,
          limit: pageSize,
          mode: "replace",
          force: true,
        })
      );
    }
  }, [
    q,
    isOfferMode,
    page,
    pageSize,
    startIndex,
    dispatch,
    selectedBrands,
    selectedCategories,
    selectedSubcategories,
  ]);

  useEffect(() => {
    if (status !== "succeeded") return;
    const total = typeof activeTotal === "number" ? activeTotal : results.length;
    modeCacheRef.current[pricingMode] = { items: results, total };
    if (results.length === 0) {
      hasNextRef.current = false;
    }
  }, [status, pricingMode, results, activeTotal]);

  useEffect(() => {
    if (status !== "succeeded") {
      if (status === "loading" || status === "idle") setNoResultModalOpen(false);
      return;
    }
    if (!q) {
      setNoResultModalOpen(false);
      setDismissedNoResultQuery("");
      return;
    }
    if (error) {
      setNoResultModalOpen(false);
      return;
    }
    if (results.length > 0) {
      setNoResultModalOpen(false);
      setDismissedNoResultQuery("");
      return;
    }
    const queryKey = String(q || "").trim();
    setNoResultModalOpen(queryKey !== dismissedNoResultQuery);
  }, [status, q, results.length, error, dismissedNoResultQuery]);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  const cachedMode = modeCacheRef.current[pricingMode];
  const otherMode = pricingMode === "Offer" ? "Base" : "Offer";
  const otherCache = modeCacheRef.current[otherMode];
  const isFirstTabLoad =
    status === "loading" && cachedMode.items.length === 0 && cachedMode.total === 0;
  const displayResults = status === "loading" ? cachedMode.items : results;
  const totalProductsCount = useMemo(() => {
    if (status === "succeeded" && results.length === 0) return 0;
    if (results.length > 0 && typeof activeTotal === "number") return activeTotal;
    if (results.length > 0) return results.length;
    if (status === "loading" && cachedMode.total > 0) return cachedMode.total;
    return typeof activeTotal === "number" ? activeTotal : cachedMode.total || 0;
  }, [status, results.length, activeTotal, cachedMode.total]);
  const showProductGrid = displayResults.length > 0;
  const hasNextPage =
    typeof activeTotal === "number"
      ? page * pageSize < activeTotal && !error && status !== "failed"
      : displayResults.length === pageSize && !error && status !== "failed";
  useEffect(() => {
    if (status !== "loading") {
      hasNextRef.current = hasNextPage;
    }
  }, [status, hasNextPage]);
  const hasNext = status === "loading" ? hasNextRef.current : hasNextPage;
  const totalPages = useMemo(
    () => getTotalPages(totalProductsCount, pageSize, page, hasNext),
    [totalProductsCount, pageSize, page, hasNext]
  );
  const showPagination =
    (totalProductsCount > 0 && (displayResults.length > 0 || status === "loading")) ||
    (isFirstTabLoad && (otherCache.total > 0 || otherCache.items.length > 0));

  const goToPage = useCallback((targetPage) => {
    setPage(targetPage);
  }, []);

  const handleSearchEdit = () => {
    const next = String(editableQuery || "").trim();
    if (!next) return;
    setDismissedNoResultQuery("");
    setNoResultModalOpen(false);
    navigate({
      pathname: location.pathname,
      search: `?q=${encodeURIComponent(next)}`,
    });
  };

  const resetFilters = () => {
    setPage(1);
    setSelectedBrands(new Set());
    setSelectedCategories(new Set());
    setSelectedSubcategories(new Set());
  };

  const activeFilterCount =
    selectedBrands.size + selectedCategories.size + selectedSubcategories.size;

  const handleAddToList = useCallback(
    async (product) => {
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }

      const lists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

      if (!lists.length) {
        throw new Error("No grocery list found. Please create a list first.");
      }

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
      } else {
        const selected = await chooseList(lists);
        if (!selected) {
          const cancelError = new Error("ADD_TO_LIST_CANCELLED");
          cancelError.code = "ADD_TO_LIST_CANCELLED";
          throw cancelError;
        }
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
      }

      if (!selectedListId) {
        throw new Error("Unable to select grocery list.");
      }

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
      if (!productId) {
        throw new Error("Invalid product.");
      }

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
          category: product?.category || product?.category_name || undefined,
          subcategory: product?.subcategory || product?.sub_category || product?.subCategory || undefined,
          platform:
            String(product?.platform || "")
              .trim()
              .toLowerCase()
              .replace("morrisons", "morrison")
              .replace("sainsburys", "sainsbury") || undefined,
          image_url: product?.images?.[0] || product?.image || product?.image_url || "",
          price: product?.price ?? null,
          weight: product?.packsize || product?.weight || "",
          quantity: 1,
        },
      });

      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }

      rememberGroceryProductView({
        listId: selectedListId,
        productId: String(productId),
        view: "single",
        platform: normalizeGroceryProductPlatform(product?.platform),
      });
    },
    [chooseList]
  );

  const categoryOptions = useMemo(() => {
    const cats = Array.isArray(allCatalog?.categories) ? allCatalog.categories : [];
    const fromCatalog = cats
      .map((c) => String(c?.category || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    if (fromCatalog.length > 0) return fromCatalog;

    const fromResults = new Set();
    displayResults.forEach((item) => {
      const v = String(item?.category || "").trim();
      if (v) fromResults.add(v);
    });
    return Array.from(fromResults).sort((a, b) => a.localeCompare(b));
  }, [allCatalog, displayResults]);

  const subcategoryOptions = useMemo(() => {
    const cats = Array.isArray(allCatalog?.categories) ? allCatalog.categories : [];
    const subs = new Set();

    if (cats.length > 0) {
      cats.forEach((c) => {
        const cat = String(c?.category || "").trim();
        if (selectedCategories.size > 0 && !selectedCategories.has(cat)) return;
        (Array.isArray(c?.subcategories) ? c.subcategories : []).forEach((sc) => {
          const v = String(sc || "").trim();
          if (v) subs.add(v);
        });
      });
      return Array.from(subs).sort((a, b) => a.localeCompare(b));
    }

    displayResults.forEach((item) => {
      const cat = String(item?.category || "").trim();
      if (selectedCategories.size > 0 && !selectedCategories.has(cat)) return;
      const sub = String(item?.subcategory || item?.sub_category || "").trim();
      if (sub) subs.add(sub);
    });
    return Array.from(subs).sort((a, b) => a.localeCompare(b));
  }, [allCatalog, selectedCategories, displayResults]);

  const renderFilters = (compact = false) => (
    <>
      <div className={compact ? "border-b border-[#E6EAF0] pb-2" : "bg-white border-b-2 border-[#E6EAF0] pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setBrandsOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Brands
          </span>
          <span className="text-[#6E7B86]">{brandsOpen ? "▴" : "▾"}</span>
        </button>
        {brandsOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {BRAND_OPTIONS.map((b) => (
              <label key={b.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedBrands.has(b.key)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPage(1);
                    setSelectedBrands((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(b.key);
                      else next.delete(b.key);
                      return next;
                    });
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{b.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={compact ? "border-b border-[#E6EAF0] pb-2" : "bg-white border-b-2 border-[#E6EAF0] pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setCategoryOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Category
          </span>
          <span className="text-[#6E7B86]">{categoryOpen ? "▴" : "▾"}</span>
        </button>
        {categoryOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {categoryOptions.length === 0 && (
              <div className="text-[12px] text-slate-400">No categories</div>
            )}
            {categoryOptions.map((c) => (
              <label key={c} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedCategories.has(c)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPage(1);
                    setSelectedCategories((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(c);
                      else next.delete(c);
                      return next;
                    });
                    setSelectedSubcategories(new Set());
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{c}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={compact ? "pb-2" : "bg-white pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setSubCategoryOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Sub Category
          </span>
          <span className="text-[#6E7B86]">{subCategoryOpen ? "▴" : "▾"}</span>
        </button>
        {subCategoryOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {subcategoryOptions.length === 0 && (
              <div className="text-[12px] text-slate-400">No sub categories</div>
            )}
            {subcategoryOptions.map((sc) => (
              <label key={sc} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSubcategories.has(sc)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPage(1);
                    setSelectedSubcategories((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(sc);
                      else next.delete(sc);
                      return next;
                    });
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{sc}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="w-full flex-1 px-3 py-4 sm:px-4 sm:py-6 md:px-6">
        {!q && (
          <div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:gap-4 sm:p-5">
            <div className="min-w-0 flex-1">
              <h1 className="text-[22px] font-bold leading-tight text-[#24303A] sm:text-[24px]">
                Search & Compare
              </h1>
              <p className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
                Start by typing a product name in the search box above.
              </p>
            </div>
          </div>
        )}

        {q && (
          <div className="flex min-w-0 gap-4 md:gap-6">
            <aside className="sticky top-24 hidden h-[calc(100vh-90px)] w-64 shrink-0 rounded-2xl border border-[#E6EAF0] bg-white p-4 text-[16px] shadow-sm md:block">
              <div className="mb-4 flex items-center justify-between border-b-2 border-[#E6EAF0] pb-4">
                <h2 className="rounded-full bg-[#7352C7] px-3 py-1 text-[16px] font-semibold text-white">Filters</h2>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-full bg-[#7352C7] px-3 py-1 text-[16px] font-semibold text-white shadow-sm"
                >
                  Reset
                </button>
              </div>
              <div className="space-y-4 overflow-y-auto pr-2 h-[calc(100%-56px)]">{renderFilters()}</div>
            </aside>

            <div className="min-w-0 flex-1 space-y-4 sm:space-y-6">
              <div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:gap-4 sm:p-5 lg:flex-row lg:items-end lg:justify-between lg:gap-6">
                <div className="min-w-0 flex-1">
                  <h1 className="text-[22px] font-bold leading-tight text-[#24303A] sm:text-[24px]">
                    Search Results
                  </h1>
                  <p className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
                    Showing results for &quot;{q}&quot;.
                  </p>
                </div>
                <ProductListSearchBar
                  value={editableQuery}
                  onChange={setEditableQuery}
                  onSearch={handleSearchEdit}
                  placeholder="Edit search text"
                />
              </div>

              <div className="md:hidden">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#E6EAF0] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#24303A] shadow-sm"
                >
                  <span>Filters</span>
                  <span className="text-[#7352C7]">
                    {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
                  </span>
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {["Base", "Offer"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      if (pricingMode === m) return;
                      setPricingMode(m);
                    }}
                    className={`rounded-full px-6 py-1.5 text-[16px] font-bold whitespace-nowrap shadow-sm ring-1 ring-[#E6EAF0] ${
                      pricingMode === m
                        ? "bg-[#7352C71A] text-[#7352C7] ring-[#7352C7]"
                        : "bg-white text-[#6E7B86]"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>

              {status === "loading" && displayResults.length === 0 && !showPagination && (
                <div className="flex justify-center py-16">
                  <span className="text-[16px] text-[#6E7B86]">Loading…</span>
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[16px] text-red-700 whitespace-pre-line">
                  {error}
                </div>
              )}

              {!error && status !== "loading" && displayResults.length === 0 && activeFilterCount > 0 && (
                <div className="flex justify-center py-16">
                  <span className="text-[16px] text-slate-500">No products match your filters.</span>
                </div>
              )}

              {showPagination && (
                <ProductsPaginationBar
                  page={page}
                  totalPages={totalPages}
                  totalProducts={totalProductsCount}
                  isLoading={status === "loading"}
                  onPageChange={goToPage}
                />
              )}

              {showProductGrid && (
                <div
                  className={`grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3${
                    status === "loading" ? " pointer-events-none opacity-60" : ""
                  }`}
                >
                  {displayResults.map((product, idx) => {
                    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? "";
                    const platformKey = normalizeStoreKey(product?.platform) || "tesco";
                    return (
                      <ProductCard
                        key={`${product.pid || product.sku}-${idx}`}
                        accent={["orange", "blue", "green", "purple"][idx % 4]}
                        product={product}
                        pricingMode={pricingMode}
                        showComparison={false}
                        onAddToList={handleAddToList}
                        onClick={() =>
                          navigate(
                            `/product-store/${encodeURIComponent(platformKey)}/${encodeURIComponent(
                              productId
                            )}`,
                            { state: { product } }
                          )
                        }
                      />
                    );
                  })}
                </div>
              )}

              {showPagination && showProductGrid && (
                <ProductsPaginationBar
                  page={page}
                  totalPages={totalPages}
                  totalProducts={totalProductsCount}
                  isLoading={status === "loading"}
                  onPageChange={goToPage}
                />
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />

      {noResultModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 text-center shadow-xl">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FF3C3F] text-[#FF3C3F]">
              <span className="text-[20px] font-bold">✕</span>
            </div>
            <h3 className="text-[24px] font-bold text-[#24303A]">Oops</h3>
            <p className="mt-2 text-[16px] text-[#6E7B86]">
              No matching result found try to change the name and try again
            </p>
            <button
              type="button"
              onClick={() => {
                setNoResultModalOpen(false);
                setDismissedNoResultQuery(String(q || "").trim());
              }}
              className="mt-5 rounded-full bg-[#FF3C3F] px-8 py-2 text-[16px] font-semibold text-white shadow-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[80] md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close filters"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full max-h-[100dvh] w-[88%] max-w-sm flex-col border-l border-[#E6EAF0] bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-[#E6EAF0] p-4 pb-3">
              <h2 className="rounded-full bg-[#7352C7] px-3 py-1 text-[14px] font-semibold text-white">Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] text-slate-600"
                aria-label="Close filters panel"
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-2 pt-2">
              {renderFilters(true)}
            </div>
            <div className="shrink-0 border-t border-[#E6EAF0] bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="w-1/2 min-w-0 rounded-full border border-[#E6EAF0] bg-white px-3 py-1 text-[16px] font-semibold text-[#24303A]"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-1/2 min-w-0 rounded-full bg-[#7352C7] px-3 py-2 text-[14px] font-semibold text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[16px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[16px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchResults;

